import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Event, ActivatedRouteSnapshot } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { BreadcrumbService } from '../../../services/utility/breadcrumb.service';

export interface BreadcrumbItem {
  label: string;
  url: string;
}

@Component({
  selector: 'gains-breadcrumb',
  standalone: false,
  templateUrl: './gains-breadcrumb.component.html',
  styleUrl: './gains-breadcrumb.component.scss'
})
export class GainsBreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs: BreadcrumbItem[] = [];
  private _routerSubscription: Subscription | undefined;

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this._routerSubscription = this._router.events.pipe(filter((event: Event) => event instanceof NavigationEnd)).subscribe(() => {
      this.breadcrumbs = this.createBreadcrumbs(this._activatedRoute.root);
      this._breadcrumbService.setBreadcrumbs(this.breadcrumbs);
    });

    // Initialize breadcrumbs on component init
    this.breadcrumbs = this.createBreadcrumbs(this._activatedRoute.root);
    this._breadcrumbService.setBreadcrumbs(this.breadcrumbs);
  }

  ngOnDestroy(): void {
    if (this._routerSubscription) {
      this._routerSubscription.unsubscribe();
    }
  }

  private createBreadcrumbs(route: ActivatedRoute, url = '', breadcrumbs: BreadcrumbItem[] = []): BreadcrumbItem[] {
    const routeSnapshot = route.snapshot;
    const currentUrl = url + routeSnapshot.url.map(segment => segment.path).join('/');

    if (route.firstChild) {
      return this.createBreadcrumbs(route.firstChild, currentUrl.length > 0 ? `${currentUrl}/` : currentUrl, breadcrumbs);
    }

    let currentRoute: ActivatedRouteSnapshot | null = routeSnapshot;

    while (currentRoute) {
      if (currentRoute.url.length > 0) {
        const routeUrl = this.buildRouteUrlSafe(currentRoute);

        const breadcrumbConfig = currentRoute.data['breadcrumb'];
        let breadcrumbValue: string | BreadcrumbItem[] | null = null;

        if (typeof breadcrumbConfig === 'function') {
          breadcrumbValue = breadcrumbConfig(currentRoute, this._router.routerState.snapshot);
        } else {
          breadcrumbValue = breadcrumbConfig || currentRoute.title || null;
        }

        const skip = currentRoute.data['skipBreadcrumb'] === true || !breadcrumbValue;

        if (!skip) {
          if (Array.isArray(breadcrumbValue)) {
            breadcrumbValue.forEach(b => {
              breadcrumbs.push({
                label: b.label,
                url: b.url
              });
            });
          } else {
            let label = breadcrumbValue as string;

            if (typeof label === 'string' && label.includes('{{')) {
              const params = currentRoute!.params;
              Object.keys(params).forEach(param => {
                label = label.replace(`{{${param}}}`, params[param]);
              });
            }

            breadcrumbs.unshift({
              label,
              url: routeUrl
            });
          }
        }
      }
      currentRoute = currentRoute.parent ?? null;
    }

    return breadcrumbs;
  }

  private buildRouteUrlSafe(route: ActivatedRouteSnapshot): string {
    let url = '';
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.url.length > 0) {
        const pathSegments = current.url.map(segment => segment.path);

        const resolvedPath = pathSegments
          .map(segment => {
            if (segment.startsWith(':')) {
              const paramName = segment.substring(1);
              return current?.params[paramName] || segment;
            }
            return segment;
          })
          .join('/');

        url = resolvedPath + (url ? '/' + url : '');
      }
      current = current.parent ?? null;
    }

    return '/' + url;
  }
}
